import { Component, EventEmitter, Input, Output, OnInit, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';

@Component({
    selector: 'app-payment-edit-modal',
    templateUrl: './payment-edit-modal.component.html',
    styleUrls: ['./payment-edit-modal.component.css'],
    standalone: false
})
export class PaymentEditModalComponent implements OnInit, OnChanges {
    @Input() isOpen = false;
    @Input() existingPaymentInfo: any = null;
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<any>();

    paymentForm: UntypedFormGroup;
    submitted = false;

    // Constants for payment types
    readonly PAYMENT_TYPE_MOBILE_MONEY = 'MOBILE_MONEY';
    readonly PAYMENT_TYPE_BANK = 'BANK';
    readonly MOBILE_PROVIDER_MTN = 'MTN_MOMO';
    readonly MOBILE_PROVIDER_ORANGE = 'ORANGE_MONEY';

    constructor(private formBuilder: UntypedFormBuilder) {
        this.paymentForm = this.formBuilder.group({
            paymentType: new UntypedFormControl('', Validators.required),
            mobileMoneyProvider: new UntypedFormControl(''),
            mobileMoneyNumber: new UntypedFormControl('', Validators.pattern(/^6[5-9]\d{7}$/)),
            bankName: new UntypedFormControl('', Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/)),
            accountNumber: new UntypedFormControl('', Validators.pattern(/^[a-zA-Z0-9]+$/)),
            accountHolderName: new UntypedFormControl('', Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/))
        });
    }

    ngOnInit(): void {
        this.initializeForm();

        // Listen for payment type changes to update validators
        this.paymentForm.get('paymentType')?.valueChanges.subscribe(paymentType => {
            this.updatePaymentValidators(paymentType);
        });
    }

    ngOnChanges(): void {
        // Re-initialize form when existingPaymentInfo changes
        if (this.isOpen) {
            this.initializeForm();
        }
    }

    private initializeForm(): void {
        if (this.existingPaymentInfo) {
            this.paymentForm.patchValue({
                paymentType: this.existingPaymentInfo.paymentType || '',
                mobileMoneyProvider: this.existingPaymentInfo.mobileMoneyProvider || '',
                mobileMoneyNumber: this.existingPaymentInfo.mobileMoneyNumber || '',
                bankName: this.existingPaymentInfo.bankName || '',
                accountNumber: this.existingPaymentInfo.accountNumber || '',
                accountHolderName: this.existingPaymentInfo.accountHolderName || ''
            });
            // Update validators based on the payment type
            this.updatePaymentValidators(this.existingPaymentInfo.paymentType);
        } else {
            this.paymentForm.reset();
        }
    }

    get f() {
        return this.paymentForm.controls;
    }

    private updatePaymentValidators(paymentType: string): void {
        const mobileProviderControl = this.paymentForm.get('mobileMoneyProvider');
        const mobileNumberControl = this.paymentForm.get('mobileMoneyNumber');
        const bankNameControl = this.paymentForm.get('bankName');
        const accountNumberControl = this.paymentForm.get('accountNumber');
        const accountHolderNameControl = this.paymentForm.get('accountHolderName');

        if (paymentType === this.PAYMENT_TYPE_MOBILE_MONEY) {
            mobileProviderControl?.setValidators(Validators.required);
            mobileNumberControl?.setValidators([
                Validators.required,
                Validators.pattern(/^6[5-9]\d{7}$/)
            ]);

            bankNameControl?.clearValidators();
            accountNumberControl?.clearValidators();
            accountHolderNameControl?.clearValidators();
        } else if (paymentType === this.PAYMENT_TYPE_BANK) {
            bankNameControl?.setValidators([
                Validators.required,
                Validators.minLength(2),
                Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/)
            ]);
            accountNumberControl?.setValidators([
                Validators.required,
                Validators.minLength(10),
                Validators.maxLength(30),
                Validators.pattern(/^[a-zA-Z0-9]+$/)
            ]);
            accountHolderNameControl?.setValidators([
                Validators.required,
                Validators.minLength(2),
                Validators.pattern(/^[a-zA-ZÀ-ÿ\s'-]+$/)
            ]);

            mobileProviderControl?.clearValidators();
            mobileNumberControl?.clearValidators();
        } else {
            mobileProviderControl?.clearValidators();
            mobileNumberControl?.clearValidators();
            bankNameControl?.clearValidators();
            accountNumberControl?.clearValidators();
            accountHolderNameControl?.clearValidators();
        }

        mobileProviderControl?.updateValueAndValidity();
        mobileNumberControl?.updateValueAndValidity();
        bankNameControl?.updateValueAndValidity();
        accountNumberControl?.updateValueAndValidity();
        accountHolderNameControl?.updateValueAndValidity();
    }

    onSubmit(): void {
        this.submitted = true;

        if (this.paymentForm.invalid) {
            return;
        }

        const formValue = this.paymentForm.value;

        const paymentInfo = {
            paymentType: formValue.paymentType,
            mobileMoneyProvider: formValue.paymentType === this.PAYMENT_TYPE_MOBILE_MONEY ? formValue.mobileMoneyProvider : null,
            mobileMoneyNumber: formValue.paymentType === this.PAYMENT_TYPE_MOBILE_MONEY ? formValue.mobileMoneyNumber : null,
            bankName: formValue.paymentType === this.PAYMENT_TYPE_BANK ? formValue.bankName : null,
            accountNumber: formValue.paymentType === this.PAYMENT_TYPE_BANK ? formValue.accountNumber : null,
            accountHolderName: formValue.paymentType === this.PAYMENT_TYPE_BANK ? formValue.accountHolderName : null
        };

        this.save.emit(paymentInfo);
        this.submitted = false;
        this.paymentForm.reset();
    }

    onClose(): void {
        this.submitted = false;
        this.paymentForm.reset();
        this.close.emit();
    }
}
